import React from "react"
import {
  DioboxEvent,
  AccountUser,
  StyleAndConfigurationSubpath,
} from "sharedTypes"
import {
  Avatar, Box, Flex, Stack, Text, VStack,
} from "@chakra-ui/react"
import SettingsSection from "components/settings/SettingsSection"
import Checkboxes from "components/ReactHookForm/Checkboxes"
import ContainedForm from "components/ReactHookForm/ContainedForm"
import { eventAndAccountAdmins } from "utilities/objects"
import RadioButtons, { RadioOptionLabel } from "components/ReactHookForm/RadioButtons"
import { Boolean } from "utilities/enums"
import UpgradeAlert from "components/elements/UpgradeAlert"
import Link from "components/elements/Link"
import { SEND_CC_MULTIPLE_ADDRESSES_ARTICLE_LINK } from "utilities/externalLinks"
import Card from "components/layout/Card"
import Button from "components/Buttons/Button"
import { Link as RouterLink } from "react-router-dom"
import { useEventStyleAndConfigurationPath } from "utilities/routes"
import useFeatureFlag from "services/featureFlags"
import useCurrentPlanType from "utilities/useCurrentPlanType"

const UserListItem = ({
  firstName, lastName, photoUrl, email,
}: {
  firstName: string; lastName: string; photoUrl?: string; email: string
}) => (
  <Flex>
    <Avatar mr={3} src={photoUrl} name={[firstName, lastName].join(" ")} />
    <Box>
      <Text fontSize="xl" fontWeight="semibold">{firstName} {lastName}</Text>
      <Text>{email}</Text>
    </Box>
  </Flex>
)

export const CC_EMAILS_TITLE = "CC Email Addresses on Guest Profile"
export const CC_EMAILS_INTRO = "When a guest has multiple email addresses:"

type Props = {
  event: DioboxEvent;
  teamMembers: AccountUser[]
  updateEvent: (params: {}) => Promise<void>;
}

const EmailNotifications = ({ event, teamMembers, updateEvent }: Props) => {
  const { corporateFeaturesEnabled } = useCurrentPlanType()
  const { vanityUrls: vanityUrlsEnabled, eventCapacity: eventCapacityEnabled } = useFeatureFlag()
  const styleAndConfigurationPath = useEventStyleAndConfigurationPath()

  const onSubmit = (values) => updateEvent(values)

  const {
    users, emailNotificationsUserIds, ccEmailsEnabled, id,
  } = event

  const allUsers = eventAndAccountAdmins(users, teamMembers)

  return (
    <Stack spacing={16}>
      <SettingsSection title="Automated Email Confirmations for Guests">
        <Card>
          <Text>Use the <strong>{vanityUrlsEnabled ? "Portal: Automated Emails" : "Forms: Email Responders"} </strong>
            to customize the automated confirmation
            emails that are sent to guests when they register for your event.
          </Text>
          <Box mt={4} textAlign="center">
            <Button
              variant="outline"
              as={RouterLink}
              to={styleAndConfigurationPath(
                id,
                StyleAndConfigurationSubpath.EmailResponders,
              )}
            >{vanityUrlsEnabled ? "Automated Email Settings" : "Email Responder Settings"}
            </Button>
          </Box>
        </Card>
      </SettingsSection>
      <CCEmails
        ccEmailsEnabled={ccEmailsEnabled}
        onSubmit={onSubmit}
        isCorporate={corporateFeaturesEnabled}
      />
      <TeamNotifications
        eventCapacityEnabled={eventCapacityEnabled}
        users={allUsers.filter((eventUser) => eventUser.user)}
        emailNotificationsUserIds={emailNotificationsUserIds}
        onSubmit={onSubmit}
      />
    </Stack>
  )
}

const TeamNotifications = ({
  users, emailNotificationsUserIds, eventCapacityEnabled, onSubmit,
}) => (
  <SettingsSection
    title="Email Notifications for your Team"
    description={`Select members on your team who should receive an email for form submissions, changes guests make to their registrations, unsubscribes${eventCapacityEnabled ? ", and when the event reaches its capacity" : ""}:`}
  >
    <ContainedForm
      values={{ emailNotificationsUserIds }}
      onSubmit={onSubmit}
      name="Email Notifications for your Team"
    >
      {({ control }) => (
        <Checkboxes
          name="emailNotificationsUserIds"
          control={control}
          items={users.map(({ user }) => user)}
          itemComponent={UserListItem}
        />
      )}
    </ContainedForm>
  </SettingsSection>
)

export const CCEmails = ({ ccEmailsEnabled, onSubmit, isCorporate }) => (
  <SettingsSection title={CC_EMAILS_TITLE} description={CC_EMAILS_INTRO}>
    <ContainedForm
      values={{ ccEmailsEnabled: ccEmailsEnabled ? Boolean.True : Boolean.False }}
      onSubmit={
        ({ ccEmailsEnabled: enabled }) => onSubmit({ ccEmailsEnabled: enabled === Boolean.True })
      }
      name="CC Email Addresses on the Guest Profile"
    >
      {({ control }) => (
        <>
          <CCEmailsFormFields control={control} isCorporate={isCorporate} />
          {!isCorporate && <UpgradeAlert text="This feature requires a Corporate Subscription." />}
        </>
      )}
    </ContainedForm>
  </SettingsSection>
)

export const CCEmailsFormFields = ({ control, isCorporate }) => (
  <VStack pt={4} spacing={8} align="baseline">
    <RadioButtons
      name="ccEmailsEnabled"
      control={control}
      spacing={6}
      options={[
        {
          label: <RadioOptionLabel
            title="Send to Primary Email Address"
            description={
              <Text>Sends the message only to the guest’s primary email address.</Text>
}
          />,
          value: Boolean.False,
        },
        {
          label: <RadioOptionLabel
            disabled={!isCorporate}
            title="Send CC to All Email Addresses"
            description={(
              <>
                <Text>
                  Sends a copy of the same message to every email address listed on the guest
                  profile.
                </Text>
                <Text>
                  This is helpful when a guest has an assistant, manager, or support team,
                  and you want everyone associated with that guest to receive the same invite
                  or RSVP link.
                </Text>
                <Text>
                  <>To learn more, see: </>
                  <Link target="_blank" href={SEND_CC_MULTIPLE_ADDRESSES_ARTICLE_LINK}>How to CC Emails to Multiple Guest Email Addresses</Link>.
                </Text>
              </>
          )}
          />,
          value: Boolean.True,
          disabled: !isCorporate,
        },
      ]}
    />
  </VStack>
)

export default EmailNotifications
